/**
 * Account-level timezone (IANA) for everything the user reads and every day boundary.
 *
 * Module state lets non-React helpers (chat timestamps, report buckets, CRM logs) stay in
 * sync with the setting loaded by AccountRegionalProvider — same pattern as `currency.ts`.
 *
 * Every `Intl` call here is guarded: `accounts.timezone` is free text, and an invalid value
 * would otherwise throw `RangeError` on every screen that renders a date.
 */

import { getActiveLocale } from '@/lib/locale'
import { DEFAULT_ACCOUNT_TIMEZONE, normalizeAccountTimezone } from '@/lib/timezones'

export { DEFAULT_ACCOUNT_TIMEZONE }

type DateLike = Date | string | number | null | undefined

let activeTimezone: string = DEFAULT_ACCOUNT_TIMEZONE
const listeners = new Set<() => void>()

export function getAccountTimezone(): string {
  return activeTimezone
}

export function setAccountTimezone(next: string): void {
  const normalized = normalizeAccountTimezone(next)
  if (normalized === activeTimezone) return
  activeTimezone = normalized
  listeners.forEach((listener) => listener())
}

export function resetAccountTimezone(): void {
  setAccountTimezone(DEFAULT_ACCOUNT_TIMEZONE)
}

export function subscribeAccountTimezone(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Accepts Date, ISO string or epoch ms. Returns null for anything unparseable. */
export function toDate(value: DateLike): Date | null {
  if (value === null || value === undefined || value === '') return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * Building an `Intl.DateTimeFormat` costs ~146µs (it resolves locale data and timezone
 * rules); reusing one costs ~9µs. The chat renders a date per message bubble and per
 * conversation row, so the constructor used to dominate every render. The instances are
 * immutable and fully described by locale + timezone + options, which makes them safe to
 * share — the key covers everything that could change the output.
 */
const formatterCache = new Map<string, Intl.DateTimeFormat>()
/** Distinct option sets are a handful in practice; the cap only guards against a leak. */
const FORMATTER_CACHE_LIMIT = 200

function getFormatter(
  locale: string,
  options: Intl.DateTimeFormatOptions,
  timeZone: string
): Intl.DateTimeFormat {
  const key = `${locale}|${timeZone}|${JSON.stringify(options)}`
  const cached = formatterCache.get(key)
  if (cached) return cached

  // Throws for an invalid timezone — the callers below catch it, same as before.
  const formatter = new Intl.DateTimeFormat(locale, { ...options, timeZone })
  if (formatterCache.size >= FORMATTER_CACHE_LIMIT) formatterCache.clear()
  formatterCache.set(key, formatter)
  return formatter
}

/**
 * A bad timezone must never take a screen down, so every formatter funnels through here
 * and retries with the default before giving up.
 */
function safeFormat(
  date: Date,
  options: Intl.DateTimeFormatOptions,
  locale: string,
  timeZone: string
): string {
  try {
    return getFormatter(locale, options, timeZone).format(date)
  } catch {
    try {
      return getFormatter(locale, options, DEFAULT_ACCOUNT_TIMEZONE).format(date)
    } catch {
      return date.toISOString()
    }
  }
}

function safeParts(
  date: Date,
  options: Intl.DateTimeFormatOptions,
  locale: string,
  timeZone: string
): Record<string, string> {
  const collect = (tz: string) => {
    const parts = getFormatter(locale, options, tz).formatToParts(date)
    const map: Record<string, string> = {}
    for (const part of parts) {
      if (part.type !== 'literal') map[part.type] = part.value
    }
    return map
  }

  try {
    return collect(timeZone)
  } catch {
    return collect(DEFAULT_ACCOUNT_TIMEZONE)
  }
}

/** Generic formatter. `timeZone` in `options` is ignored — the account zone always wins. */
export function formatInAccountTz(
  value: DateLike,
  options: Intl.DateTimeFormatOptions = {},
  locale: string = getActiveLocale(),
  timeZone: string = activeTimezone
): string {
  const date = toDate(value)
  if (!date) return ''
  return safeFormat(date, options, locale, timeZone)
}

export function formatDateInAccountTz(
  value: DateLike,
  options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' },
  locale: string = getActiveLocale(),
  timeZone: string = activeTimezone
): string {
  return formatInAccountTz(value, options, locale, timeZone)
}

export function formatTimeInAccountTz(
  value: DateLike,
  options: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' },
  locale: string = getActiveLocale(),
  timeZone: string = activeTimezone
): string {
  return formatInAccountTz(value, options, locale, timeZone)
}

export function formatDateTimeInAccountTz(
  value: DateLike,
  options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  },
  locale: string = getActiveLocale(),
  timeZone: string = activeTimezone
): string {
  return formatInAccountTz(value, options, locale, timeZone)
}

/**
 * Calendar day (`YYYY-MM-DD`) of an instant in the account timezone.
 * This is the unit every bucket, "today/yesterday" label and day filter must agree on.
 */
export function accountDayKey(value: DateLike, timeZone: string = activeTimezone): string {
  const date = toDate(value)
  if (!date) return ''

  const parts = safeParts(
    date,
    { year: 'numeric', month: '2-digit', day: '2-digit' },
    'en-CA',
    timeZone
  )

  return `${parts.year}-${parts.month}-${parts.day}`
}

/**
 * Formats a plain `YYYY-MM-DD` for display, without any timezone conversion.
 *
 * Day keys are already resolved calendar days (report buckets, date inputs). Running them
 * through the account zone would shift them: `2026-08-13` parsed as an instant and rendered
 * in another zone can come out as the 12th.
 */
export function formatDayKey(
  dayKey: string,
  options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' },
  locale: string = getActiveLocale()
): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) return dayKey ?? ''
  const [year, month, day] = dayKey.split('-').map(Number)
  const asUtc = new Date(Date.UTC(year, month - 1, day, 12))
  return safeFormat(asUtc, options, locale, 'UTC')
}

/** Offset of `timeZone` at `date`, in ms (DST-aware). */
function offsetMs(date: Date, timeZone: string): number {
  const parts = safeParts(
    date,
    {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    },
    'en-US',
    timeZone
  )

  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour === '24' ? '0' : parts.hour),
    Number(parts.minute),
    Number(parts.second)
  )

  return asUtc - date.getTime()
}

/**
 * First instant of the calendar day `dayKey` in `timeZone`.
 *
 * Two candidates are built, because the offset at UTC midnight can belong to the other
 * side of a DST change: one using that offset, one using the offset at the instant it
 * points to. Which of the two is right depends on the direction of the change, so the
 * choice is made by checking the answer rather than by predicting it — the earliest
 * candidate that actually lands on `dayKey` wins.
 *
 * The three cases this has to cover, all reachable from ACCOUNT_TIMEZONE_OPTIONS:
 *
 * - Ordinary day: both candidates agree.
 * - Clock goes back at midnight (Santiago in April): the first candidate lands at 23:00
 *   of the previous day, the second on midnight.
 * - Clock goes forward at midnight (Santiago in September, Havana in March, Asunción in
 *   October): midnight does not exist — the clock jumps from 23:59 straight to 01:00.
 *   The second candidate lands at 23:00 of the previous day, so the day would start
 *   before it began and `endOfAccountDay` would come out *earlier* than the start,
 *   producing an inverted range that returns nothing. The first candidate lands on 01:00,
 *   which is the first instant that day actually has.
 */
function startOfDayFromKey(dayKey: string, timeZone: string): Date {
  const utcMidnight = new Date(`${dayKey}T00:00:00.000Z`)
  const fromUtcOffset = new Date(utcMidnight.getTime() - offsetMs(utcMidnight, timeZone))
  const fromLocalOffset = new Date(utcMidnight.getTime() - offsetMs(fromUtcOffset, timeZone))

  const candidates =
    fromUtcOffset.getTime() <= fromLocalOffset.getTime()
      ? [fromUtcOffset, fromLocalOffset]
      : [fromLocalOffset, fromUtcOffset]

  for (const candidate of candidates) {
    if (accountDayKey(candidate, timeZone) === dayKey) return candidate
  }

  // Unreachable for any real IANA zone; keeps the previous answer for a bad one.
  return fromLocalOffset
}

/**
 * Midnight in the account timezone for a plain `YYYY-MM-DD` — the shape `<input type="date">`
 * gives back, which carries no zone of its own.
 */
export function startOfAccountDayFromKey(
  dayKey: string,
  timeZone: string = activeTimezone
): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) return null
  return startOfDayFromKey(dayKey, timeZone)
}

/** Instant of midnight in the account timezone, for the calendar day `value` falls on. */
export function startOfAccountDay(value: DateLike, timeZone: string = activeTimezone): Date {
  const date = toDate(value) ?? new Date()
  const dayKey = accountDayKey(date, timeZone)
  if (!dayKey) return date

  return startOfDayFromKey(dayKey, timeZone)
}

/** Last millisecond of the account-timezone day — mirrors date-fns `endOfDay` semantics. */
export function endOfAccountDay(value: DateLike, timeZone: string = activeTimezone): Date {
  const start = startOfAccountDay(value, timeZone)
  // 26h lands safely inside the next day even across a DST jump; re-anchoring gives its start.
  const nextDayStart = startOfAccountDay(new Date(start.getTime() + 26 * 60 * 60 * 1000), timeZone)
  return new Date(nextDayStart.getTime() - 1)
}

/** Start of the account-timezone month containing `value`. */
export function startOfAccountMonth(value: DateLike, timeZone: string = activeTimezone): Date {
  const dayKey = accountDayKey(toDate(value) ?? new Date(), timeZone)
  if (!dayKey) return startOfAccountDay(value, timeZone)
  const [year, month] = dayKey.split('-')
  return startOfDayFromKey(`${year}-${month}-01`, timeZone)
}

/** Last millisecond of the account-timezone month containing `value`. */
export function endOfAccountMonth(value: DateLike, timeZone: string = activeTimezone): Date {
  const monthStart = startOfAccountMonth(value, timeZone)
  // Day 28 + 4 days always lands in the next month, whatever its length.
  const nextMonthStart = startOfAccountMonth(
    new Date(monthStart.getTime() + 32 * 24 * 60 * 60 * 1000),
    timeZone
  )
  return new Date(nextMonthStart.getTime() - 1)
}

/**
 * Adds (or subtracts) whole months keeping the day of month, clamped to the target
 * month's length — the 31st of a month maps to the 30th (or 28th) of a shorter one.
 */
export function addAccountMonths(
  value: DateLike,
  months: number,
  timeZone: string = activeTimezone
): Date {
  const dayKey = accountDayKey(toDate(value) ?? new Date(), timeZone)
  if (!dayKey) return startOfAccountDay(value, timeZone)

  const [year, month, day] = dayKey.split('-').map(Number)
  const totalMonths = year * 12 + (month - 1) + months
  const targetYear = Math.floor(totalMonths / 12)
  const targetMonth = totalMonths - targetYear * 12 + 1

  // Day 0 of the next month is the last day of the target one.
  const daysInTargetMonth = new Date(Date.UTC(targetYear, targetMonth, 0)).getUTCDate()
  const clampedDay = Math.min(day, daysInTargetMonth)

  const pad = (n: number) => String(n).padStart(2, '0')
  return startOfDayFromKey(`${targetYear}-${pad(targetMonth)}-${pad(clampedDay)}`, timeZone)
}

/** Whole calendar days between two instants, counted in the account timezone. */
export function differenceInAccountDays(
  later: DateLike,
  earlier: DateLike,
  timeZone: string = activeTimezone
): number {
  const a = startOfAccountDay(later, timeZone).getTime()
  const b = startOfAccountDay(earlier, timeZone).getTime()
  // Rounded, not floored: consecutive local midnights are 23h or 25h apart across DST.
  return Math.round((a - b) / (24 * 60 * 60 * 1000))
}

/** Adds `days` calendar days in the account timezone (DST-safe). */
export function addAccountDays(
  value: DateLike,
  days: number,
  timeZone: string = activeTimezone
): Date {
  const start = startOfAccountDay(value, timeZone)
  const shifted = new Date(start.getTime() + days * 24 * 60 * 60 * 1000)
  return startOfAccountDay(shifted, timeZone)
}

/** True when both instants fall on the same calendar day in the account timezone. */
export function isSameAccountDay(
  a: DateLike,
  b: DateLike,
  timeZone: string = activeTimezone
): boolean {
  const keyA = accountDayKey(a, timeZone)
  return Boolean(keyA) && keyA === accountDayKey(b, timeZone)
}
